import React, { useEffect, useState } from "react";
import { ArrowLeft, MessageSquareLock, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { api, errMsg } from "../lib/api";

export const SUPER_DEVICE_KEY = "mc_super_device";

export const getSuperDeviceToken = () => {
  try { return localStorage.getItem(SUPER_DEVICE_KEY) || null; } catch { return null; }
};
export const setSuperDeviceToken = (token) => {
  try { token ? localStorage.setItem(SUPER_DEVICE_KEY, token) : localStorage.removeItem(SUPER_DEVICE_KEY); } catch { /* ignore */ }
};

/**
 * Step 2 of the Super Admin sign-in: the 6-digit code sent by SMS to the registered mobile.
 *   challenge  — the `two_step: "required"` payload returned by POST /platform/auth/login
 *   onVerified — receives { token, user, device_token? }
 *   onBack     — return to the email / password form
 */
export function SuperOtpStep({ challenge, onVerified, onBack }) {
  const [current, setCurrent] = useState(challenge);
  const [otp, setOtp] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [left, setLeft] = useState(challenge?.expires_in || 300);
  const [resendIn, setResendIn] = useState(challenge?.resend_after || 30);

  useEffect(() => { setCurrent(challenge); setOtp(""); setLeft(challenge?.expires_in || 300); setResendIn(challenge?.resend_after || 30); }, [challenge]);
  useEffect(() => {
    const t = setInterval(() => { setLeft((s) => Math.max(0, s - 1)); setResendIn((s) => Math.max(0, s - 1)); }, 1000);
    return () => clearInterval(t);
  }, []);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const verify = async (code) => {
    const value = (code ?? otp).trim();
    if (value.length !== 6 || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/platform/auth/login/verify-otp", { challenge_id: current.challenge_id, otp: value, remember_device: remember });
      onVerified(r.data);
    } catch (err) {
      toast.error(errMsg(err));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    try {
      const r = await api.post("/platform/auth/login/resend-otp", { challenge_id: current.challenge_id });
      setCurrent(r.data);
      setOtp("");
      setLeft(r.data.expires_in || 300);
      setResendIn(r.data.resend_after || 30);
      toast.success("A new code has been sent");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-4 mt-2" data-testid="super-otp-step">
      <div className="flex items-start gap-2.5 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2.5">
        <MessageSquareLock className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
        <div>
          <p className="font-medium text-foreground">Two-step sign-in</p>
          <p>We sent a 6-digit code by SMS to <span className="font-mono text-foreground" data-testid="super-otp-mobile-hint">{current?.mobile_hint}</span>.
            {" "}It expires in <span className="font-mono text-foreground" data-testid="super-otp-countdown">{mmss(left)}</span>.</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3">
        <InputOTP maxLength={6} value={otp} onChange={(v) => { setOtp(v); if (v.length === 6) verify(v); }}
          disabled={busy || left === 0} autoFocus data-testid="super-otp-input" containerClassName="justify-center">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-10 text-lg" />)}
          </InputOTPGroup>
        </InputOTP>
        {left === 0 && <p className="text-xs text-destructive" data-testid="super-otp-expired">This code has expired — request a new one.</p>}
      </div>
      <label className="flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer hover:bg-muted/50" data-testid="super-otp-remember">
        <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="mt-0.5" data-testid="super-otp-remember-checkbox" />
        <span className="text-xs">
          <span className="font-medium block">Don't ask again on this browser for 30 days</span>
          <span className="text-muted-foreground">Only tick this on a device you own. You can sign browsers out from My account → Security.</span>
        </span>
      </label>
      <Button type="button" className="w-full" disabled={otp.length !== 6 || busy || left === 0} onClick={() => verify()} data-testid="super-otp-verify-button">
        <ShieldCheck className="h-4 w-4 mr-2" /> {busy ? "Verifying..." : "Verify & sign in"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="super-otp-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </button>
        <button type="button" onClick={resend} disabled={resendIn > 0 || resending}
          className="inline-flex items-center gap-1 text-primary disabled:text-muted-foreground disabled:cursor-not-allowed" data-testid="super-otp-resend">
          <RefreshCw className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`} /> {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
        </button>
      </div>
      <Label className="sr-only">One-time code</Label>
    </div>
  );
}
